/**
 * Collapsible — minimal click-to-expand disclosure primitive.
 *
 * Manager Deal-Health dashboard intelligence surfaces (plan
 * 2026-07-15-016-deal-intelligence-terra-plan.md, Unit 7) needed a shared
 * expando for the "Threading" column's engaged-contact evidence list, and the
 * plan calls out the follow-up MEDDPICC unit will reuse it — hence `shared/`,
 * not colocated in `manager/`. Repo-wide search (`web/app-home/src/components/
 * shared/`) found no existing Collapsible/Accordion primitive; `DraftPreviewPane`
 * was the closest hit but is a fixed-open preview pane, not a toggleable
 * disclosure.
 *
 * Deliberately click-to-toggle rather than CSS `:hover` — hover-only
 * disclosures aren't reachable by keyboard/touch and aren't `fireEvent.click`-
 * testable, both of which matter more here than a pure "hover" affordance.
 * `stopPropagation` on the trigger click matters when this is mounted inside
 * a clickable table row (e.g. `DealHealthView`'s row `onClick` opens the chat
 * sidecar) — toggling the disclosure must never also fire the row's own click
 * handler, mirroring `aiColumns.tsx`'s `chat` button's identical guard.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

export interface CollapsibleProps {
  /** Always-visible control — rendered inside the toggle `<button>`. */
  trigger: ReactNode;
  /** Rendered only while expanded. */
  children: ReactNode;
  /** Accessible name for the toggle button (there is no visible "Expand" text). */
  triggerAriaLabel: string;
  /** Starts expanded. Defaults to collapsed. */
  defaultOpen?: boolean;
  /** Wrapper `className` (the outer `<div>`, not the trigger or content). */
  className?: string;
  /** Optional `data-testid` on the toggle `<button>`, for callers that need a stable test hook beyond the accessible name. */
  triggerTestId?: string;
}

export function Collapsible({
  trigger,
  children,
  triggerAriaLabel,
  defaultOpen = false,
  className,
  triggerTestId,
}: CollapsibleProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={triggerAriaLabel}
        data-testid={triggerTestId}
        onClick={(e) => {
          // See module doc comment — must not bubble to a clickable ancestor
          // row (DealHealthView mounts this inside a row with its own onClick).
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="inline-flex items-center gap-1 text-left"
      >
        {trigger}
        {open ? (
          <ChevronDownIcon className="w-3 h-3 flex-shrink-0 text-icon-subtle" />
        ) : (
          <ChevronRightIcon className="w-3 h-3 flex-shrink-0 text-icon-subtle" />
        )}
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}
